/**
 * useTrendCarouselItems — provides items for the homepage TrendCarousel.
 *
 * Currently returns mock data from trendData.ts (CX-2308).
 *
 * TO SWITCH TO API:
 *   Replace the return statement with the API-driven mapping, e.g.:
 *
 *   const [data, error, isLoading] = useLandingPage(settings.language.toLowerCase());
 *   const items = useMemo(() => {
 *     if (!data?.data?.length) return [];
 *     return data.data.reduce<TrendCarouselItem[]>((acc, item) => {
 *       const url = item?.link?.url ?? "";
 *       const image = item?.teaserImage ?? "";
 *       if (!image && !url) return acc;
 *       acc.push({ id: item?.carouselOrder?.toString() ?? `trend-${acc.length}`, title: item?.title ?? "", image, url });
 *       return acc;
 *     }, []);
 *   }, [data]);
 *   return { items, isLoading, error };
 *
 * The hook signature and return type are unchanged, so no call sites need to update.
 */

import { useFeatureFlags } from "hooks/use-config";
import { cmfGuideData } from "pages/circular-cmf-guide/cmfGuideData";
import { trendCarouselItems } from "pages/trend/trendData";
import { useMemo } from "react";
import type { TrendCarouselItem } from "./trendCarousel.types";

interface UseTrendCarouselItemsResult {
  items: TrendCarouselItem[];
  isLoading: boolean;
  error: Error | undefined;
}

// Circular CMF Guide is a standalone feature (not a trend entry). Its carousel
// entry is gated by the same `circularCmfGuide` flag as its route, and sits
// second — right after CMF Design Trends 2025+ (mirrors demo-v784 InspireSection).
const circularCmfCarouselItem: TrendCarouselItem = {
  id: cmfGuideData.slug,
  title: "Circular CMF Guide",
  image: cmfGuideData.hero.image,
  url: `/${cmfGuideData.slug}`,
};

export const useTrendCarouselItems = (): UseTrendCarouselItemsResult => {
  const flags = useFeatureFlags();

  const items = useMemo(() => {
    if (!flags.circularCmfGuide) {
      return trendCarouselItems;
    }
    return [
      trendCarouselItems[0],
      circularCmfCarouselItem,
      ...trendCarouselItems.slice(1),
    ].filter(Boolean);
  }, [flags.circularCmfGuide]);

  return {
    items,
    isLoading: false,
    error: undefined,
  };
};
